<template>
  <div class="feedback-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="page-title">
        <i class="bi bi-chat-square-text me-2"></i>
        反馈管理
      </h2>
      <p class="page-description">管理用户提交的题目反馈和问题报告</p>
    </div>

    <!-- 筛选器 -->
    <div class="filters-section">
      <div class="row g-3">
        <div class="col-md-3">
          <label class="form-label">反馈状态</label>
          <select v-model="statusFilter" class="form-select" @change="loadFeedbacks">
            <option value="">全部状态</option>
            <option value="pending">待处理</option>
            <option value="processing">处理中</option>
            <option value="resolved">已解决</option>
            <option value="rejected">已拒绝</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">反馈类型</label>
          <select v-model="typeFilter" class="form-select" @change="loadFeedbacks">
            <option value="">全部类型</option>
            <option value="question_error">题目描述有误</option>
            <option value="answer_error">答案有误</option>
            <option value="explanation_error">解析有误</option>
            <option value="other">其他问题</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="form-label">搜索</label>
          <input 
            v-model="searchQuery" 
            type="text" 
            class="form-control" 
            placeholder="搜索用户名或反馈内容"
            @input="debounceSearch"
          >
        </div>
        <div class="col-md-2">
          <label class="form-label">&nbsp;</label>
          <button class="btn btn-outline-secondary d-block w-100" @click="resetFilters">
            <i class="bi bi-arrow-clockwise me-1"></i>
            重置
          </button>
        </div>
      </div>
    </div>

    <!-- 反馈列表 -->
    <div class="feedback-list">
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">加载中...</span>
        </div>
        <p class="mt-2 text-muted">加载反馈数据中...</p>
      </div>

      <div v-else-if="feedbacks.length === 0" class="empty-state">
        <div class="text-center py-5">
          <i class="bi bi-inbox display-1 text-muted"></i>
          <h4 class="mt-3 text-muted">暂无反馈数据</h4>
          <p class="text-muted">当前筛选条件下没有找到相关反馈</p>
        </div>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover">
          <thead class="table-light">
            <tr>
              <th>ID</th>
              <th>用户</th>
              <th class="col-name">题目</th>
              <th>类型</th>
              <th>状态</th>
              <th>反馈内容</th>
              <th>提交时间</th>
              <th class="col-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="feedback in feedbacks" :key="feedback.id">
              <td>{{ feedback.id }}</td>
              <td>
                <div class="user-info">
                  <strong>{{ feedback.user?.username || '未知用户' }}</strong>
                  <small v-if="feedback.user?.firstName" class="text-muted d-block">
                    {{ feedback.user.firstName }} {{ feedback.user.lastName || '' }}
                  </small>
                </div>
              </td>
              <td class="col-name">
                <div class="question-info">
                  <small v-if="feedback.questionId" class="text-muted">
                    ID: <button class="btn btn-link btn-sm p-0 text-decoration-none" @click="showQuestionDetail(feedback.questionId)" title="点击查看题目详情">{{ feedback.questionId }}</button>
                  </small>
                  <small v-else class="text-muted">非题目反馈</small>
                  <div v-if="feedback.question" class="question-preview">
                    {{ truncateText(feedback.question.question, 50) }}
                  </div>
                  <small v-if="feedback.question?.questionBank" class="badge bg-light text-dark">
                    {{ feedback.question.questionBank.domain }} - {{ feedback.question.questionBank.subdomain }}
                  </small>
                </div>
              </td>
              <td>
                <span class="badge" :class="getTypeBadgeClass(feedback.type)">
                  {{ getTypeLabel(feedback.type) }}
                </span>
              </td>
              <td>
                <span class="badge" :class="getStatusBadgeClass(feedback.status)">
                  {{ getStatusLabel(feedback.status) }}
                </span>
              </td>
              <td>
                <div class="content-preview">
                  {{ truncateText(feedback.content, 80) }}
                </div>
              </td>
              <td>
                <small class="text-muted">
                  {{ formatDate(feedback.createdAt) }}
                </small>
              </td>
              <td class="col-actions">
                <div class="btn-group-text">
                  <button 
                    class="btn-text text-primary"
                    @click="viewFeedback(feedback)"
                    title="查看详情"
                  >
                    查看
                  </button>
                  <button 
                    class="btn-text text-success"
                    @click="handleFeedback(feedback)"
                    title="处理反馈"
                    :disabled="feedback.status === 'resolved'"
                  >
                    处理
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 分页 -->
      <nav v-if="totalPages > 1" class="mt-4">
        <ul class="pagination justify-content-center mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(1)" :disabled="currentPage === 1" title="首页">
              <span aria-hidden="true">&laquo;</span>
            </button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1" title="上一页">
              <span aria-hidden="true">&lsaquo;</span>
            </button>
          </li>
          <li 
            v-for="(page, index) in visiblePages" 
            :key="index"
            class="page-item" 
            :class="{ active: page === currentPage, disabled: page === '...' }"
          >
            <span v-if="page === '...'" class="page-link el-pagination-ellipsis">
              <span aria-hidden="true">...</span>
            </span>
            <button v-else class="page-link" @click="changePage(Number(page))">
              {{ page }}
            </button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" title="下一页">
              <span aria-hidden="true">&rsaquo;</span>
            </button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="changePage(totalPages)" :disabled="currentPage === totalPages" title="尾页">
              <span aria-hidden="true">&raquo;</span>
            </button>
          </li>
        </ul>
      </nav>
    </div>

    <!-- 反馈详情模态框 -->
    <div v-if="showDetailModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-chat-square-text me-2"></i>
              反馈详情 #{{ selectedFeedback?.id }}
            </h5>
            <button type="button" class="btn-close" @click="closeDetailModal"></button>
          </div>
          <div class="modal-body" v-if="selectedFeedback">
            <div class="row">
              <div class="col-md-6">
                <h6>基本信息</h6>
                <table class="table table-sm">
                  <tr>
                    <td><strong>反馈ID:</strong></td>
                    <td>{{ selectedFeedback.id }}</td>
                  </tr>
                  <tr>
                    <td><strong>用户:</strong></td>
                    <td>{{ selectedFeedback.user?.username || '未知用户' }}</td>
                  </tr>
                  <tr>
                    <td><strong>题目ID:</strong></td>
                    <td>{{ selectedFeedback.questionId || '非题目反馈' }}</td>
                  </tr>
                  <tr>
                    <td><strong>反馈类型:</strong></td>
                    <td>
                      <span class="badge" :class="getTypeBadgeClass(selectedFeedback.type)">
                        {{ getTypeLabel(selectedFeedback.type) }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>状态:</strong></td>
                    <td>
                      <span class="badge" :class="getStatusBadgeClass(selectedFeedback.status)">
                        {{ getStatusLabel(selectedFeedback.status) }}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>提交时间:</strong></td>
                    <td>{{ formatDate(selectedFeedback.createdAt) }}</td>
                  </tr>
                </table>
              </div>
              <div class="col-md-6" v-if="selectedFeedback.question">
                <h6>相关题目</h6>
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start gap-3">
                      <div class="flex-grow-1">
                    <p class="card-text">{{ selectedFeedback.question.question }}</p>
                    <small class="text-muted" v-if="selectedFeedback.question.questionBank">
                      {{ selectedFeedback.question.questionBank.domain }} - {{ selectedFeedback.question.questionBank.subdomain }}
                    </small>
                      </div>
                      <div class="d-flex flex-column gap-2 flex-shrink-0">
                        <button
                          v-if="selectedFeedback.questionId"
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="showQuestionDetail(selectedFeedback.questionId, true)"
                        >
                          <i class="bi bi-pencil me-1"></i>编辑题目
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div class="mt-4">
              <h6>反馈内容</h6>
              <div class="card">
                <div class="card-body">
                  <p class="card-text">{{ selectedFeedback.content }}</p>
                </div>
              </div>
            </div>

            <div class="mt-4">
              <h6>管理员回复</h6>
              <div v-if="selectedFeedback.adminReply" class="card bg-light">
                <div class="card-body">
                  <p class="card-text">{{ selectedFeedback.adminReply }}</p>
                  <small class="text-muted" v-if="selectedFeedback.handler">
                    回复人: {{ selectedFeedback.handler.username }}
                    <span v-if="selectedFeedback.handledAt"> - {{ formatDate(selectedFeedback.handledAt) }}</span>
                  </small>
                </div>
              </div>
              <div v-else class="text-center py-4">
                <i class="bi bi-chat-dots text-muted" style="font-size: 2rem;"></i>
                <p class="mt-2 text-muted">暂无管理员回复</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeDetailModal">关闭</button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="handleFeedback(selectedFeedback)"
              :disabled="selectedFeedback?.status === 'resolved'"
            >
              处理反馈
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 处理反馈模态框 -->
    <div v-if="showHandleModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-gear me-2"></i>
              处理反馈 #{{ selectedFeedback?.id }}
            </h5>
            <button type="button" class="btn-close" @click="closeHandleModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">处理状态</label>
              <select v-model="handleForm.status" class="form-select">
                <option value="processing">处理中</option>
                <option value="resolved">已解决</option>
                <option value="rejected">已拒绝</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">管理员回复</label>
              <textarea 
                v-model="handleForm.adminReply" 
                class="form-control" 
                rows="4"
                placeholder="请输入处理结果或回复内容"
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeHandleModal">取消</button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="submitHandle"
              :disabled="saving || !handleForm.status"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ saving ? '处理中...' : '确认处理' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 题目详情模态框 -->
    <div v-if="showQuestionModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header d-flex justify-content-between align-items-center">
            <h5 class="modal-title mb-0">
              <i class="bi bi-question-circle me-2"></i>
              题目详情 #{{ selectedQuestion?.id }}
            </h5>
            <div class="d-flex gap-2 align-items-center">
              <button v-if="selectedQuestion && !questionNotFound && !editingQuestion" 
                      type="button" 
                      class="btn btn-outline-primary btn-sm" 
                      @click="toggleEditMode" aria-label="编辑题目">
                <i class="bi bi-pencil me-1"></i>编辑
              </button>
              <button v-if="editingQuestion" 
                      type="button" 
                      class="btn btn-success btn-sm" 
                      @click="saveQuestionEdit"
                      :disabled="loadingQuestion" aria-label="保存题目">
                <i class="bi bi-check me-1"></i>保存
              </button>
              <button v-if="editingQuestion" 
                      type="button" 
                      class="btn btn-outline-secondary btn-sm" 
                      @click="cancelEdit">
                <i class="bi bi-x me-1"></i>取消
              </button>
              <button type="button" class="btn-close" @click="closeQuestionModal"></button>
            </div>
          </div>
          <div class="modal-body">
            <!-- 加载状态 -->
            <div v-if="loadingQuestion" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">加载中...</span>
              </div>
              <p class="mt-2 text-muted">正在获取题目详情...</p>
            </div>
            
            <!-- 题目不存在/已删除状态 -->
            <div v-else-if="questionNotFound" class="text-center py-5">
              <div class="mb-3">
                <i class="bi bi-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
              </div>
              <h5 class="text-warning">题目不存在</h5>
              <p class="text-muted">该题目可能已被删除或不存在，请检查题目ID是否正确。</p>
              <button type="button" class="btn btn-secondary" @click="closeQuestionModal">
                <i class="bi bi-arrow-left me-1"></i>返回
              </button>
            </div>
            
            <!-- 题目详情内容 -->
            <div v-else-if="selectedQuestion" class="row">
              <!-- 题目基本信息 -->
              <div class="col-md-4">
                <h6>基本信息</h6>
                <table class="table table-sm">
                  <tr>
                    <td><strong>题目ID:</strong></td>
                    <td>{{ selectedQuestion.id }}</td>
                  </tr>
                  <tr>
                    <td><strong>所属题库:</strong></td>
                    <td>
                      <span v-if="selectedQuestionBank" class="badge bg-primary">
                        {{ selectedQuestionBank.description }}
                      </span>
                      <span v-else-if="selectedQuestion.questionBankId" class="text-muted">
                        题库ID: {{ selectedQuestion.questionBankId }}
                      </span>
                      <span v-else class="text-muted">未知</span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>题型:</strong></td>
                    <td>
                      <select v-if="editingQuestion && (selectedQuestion.type === 'singleChoice' || selectedQuestion.type === 'multipleChoice')"
                              v-model="selectedQuestion.type" class="form-select form-select-sm" aria-label="题型">
                        <option value="singleChoice">单选题</option>
                        <option value="multipleChoice">多选题</option>
                      </select>
                      <span v-else class="badge bg-info" aria-label="当前题型">{{ getQuestionTypeText(selectedQuestion.type) }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>章节:</strong></td>
                    <td>{{ selectedQuestion.chapter }}</td>
                  </tr>
                  <tr>
                      <td><strong>难度:</strong></td>
                      <td>
                        <span class="badge" :class="`bg-${getDifficultyText(selectedQuestion.difficulty).class}`">
                          {{ getDifficultyText(selectedQuestion.difficulty).text }}
                        </span>
                      </td>
                    </tr>
                  <tr>
                    <td><strong>状态:</strong></td>
                    <td>
                      <span class="badge" :class="selectedQuestion.isActive ? 'bg-success' : 'bg-secondary'">
                        {{ selectedQuestion.isActive ? '启用' : '禁用' }}
                      </span>
                    </td>
                  </tr>
                </table>
              </div>
              
              <!-- 题目内容 -->
              <div class="col-md-8">
                <h6>题目内容</h6>
                <div class="card mb-3">
                  <div class="card-body">
                    <div v-if="!editingQuestion">
                      <p class="card-text">{{ selectedQuestion.question }}</p>
                    </div>
                    <div v-else>
                      <textarea 
                        v-model="selectedQuestion.question" 
                        class="form-control" 
                        rows="4" 
                        placeholder="请输入题目内容">
                      </textarea>
                    </div>
                  </div>
                </div>
                
                <!-- 选项（如果有） -->
                <div v-if="selectedQuestion.options && Object.keys(selectedQuestion.options).length > 0" class="mb-3">
                  <h6>选项</h6>
                  <div class="card">
                    <div class="card-body">
                      <div v-if="!editingQuestion">
                        <div v-for="(option, key) in selectedQuestion.options" :key="key" class="mb-2">
                          <strong>{{ key }}.</strong> {{ option }}
                        </div>
                      </div>
                      <div v-else>
                        <div v-for="(option, key) in selectedQuestion.options" :key="key" class="mb-3">
                          <label class="form-label"><strong>选项 {{ key }}:</strong></label>
                          <input 
                            v-model="selectedQuestion.options[key]" 
                            type="text" 
                            class="form-control" 
                            :placeholder="`请输入选项${key}内容`">
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 正确答案 -->
                <div class="mb-3">
                  <h6>正确答案</h6>
                  <div class="card bg-success bg-opacity-10">
                    <div class="card-body">
                      <div v-if="!editingQuestion">
                        <p class="card-text text-success fw-bold mb-0">
                          {{ formatAnswer(selectedQuestion.answer, selectedQuestion.options) }}
                        </p>
                      </div>
                      <div v-else>
                        <input 
                          v-model="selectedQuestion.answer" 
                          type="text" 
                          class="form-control" 
                          placeholder="请输入正确答案（多个答案用逗号分隔）">
                        <small class="form-text text-muted mt-1">
                          提示：单选题填写选项字母（如：A），多选题用逗号分隔（如：A,B,C）
                        </small>
                        <div v-if="getChoiceAnswerValidationMessage(selectedQuestion)"
                             class="alert alert-danger mt-2 mb-0" role="alert" aria-live="polite">
                          <i class="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>{{ getChoiceAnswerValidationMessage(selectedQuestion) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 解析 -->
                <div v-if="selectedQuestion.explanation || editingQuestion" class="mb-3">
                  <h6>题目解析</h6>
                  <div class="card bg-light">
                    <div class="card-body">
                      <div v-if="!editingQuestion">
                        <p class="card-text">{{ selectedQuestion.explanation }}</p>
                      </div>
                      <div v-else>
                        <textarea 
                          v-model="selectedQuestion.explanation" 
                          class="form-control" 
                          rows="3" 
                          placeholder="请输入题目解析（可选）">
                        </textarea>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeQuestionModal">关闭</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 模态框背景 -->
    <div v-if="showDetailModal || showHandleModal || showQuestionModal" class="modal-backdrop fade show"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { feedbackApi, type Feedback } from '../../api/feedback'
import { questionBanksApi } from '../../api/questionBanks'
import { useToast } from 'vue-toastification'
import type { Question } from '../../types'
import { getQuestionTypeText } from '../../utils/questionTypes'
import { getChoiceAnswerValidationMessage } from '../../utils/questionRevision'

const toast = useToast()

// 响应式数据
const feedbacks = ref<Feedback[]>([])
const loading = ref(false)
const saving = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const totalCount = ref(0)

// 筛选器
const statusFilter = ref('')
const typeFilter = ref('')
const searchQuery = ref('')

// 模态框状态
const showDetailModal = ref(false)
const showHandleModal = ref(false)
const showQuestionModal = ref(false)
const selectedFeedback = ref<Feedback | null>(null)
const selectedQuestion = ref<Question | null>(null)
const selectedQuestionBank = ref<any | null>(null)
const loadingQuestion = ref(false)
const editingQuestion = ref(false)
const questionNotFound = ref(false)

// 处理表单
const handleForm = ref({
  status: 'processing' as 'processing' | 'resolved' | 'rejected',
  adminReply: ''
})

// 计算属性
const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

const visiblePages = computed(() => {
  const pages: (number | string)[] = []
  const current = currentPage.value
  const total = totalPages.value
  const maxPages = 7 // 最大显示页码数

  if (total <= maxPages) {
    for (let i = 1; i <= total; i++) {
      pages.push(i)
    }
  } else {
    if (current < 5) {
      for (let i = 1; i <= 6; i++) pages.push(i)
      pages.push('...')
      pages.push(total)
    } else if (current > total - 4) {
      pages.push(1)
      pages.push('...')
      for (let i = total - 5; i <= total; i++) pages.push(i)
    } else {
      pages.push(1)
      pages.push('...')
      for (let i = current - 2; i <= current + 2; i++) pages.push(i)
      pages.push('...')
      pages.push(total)
    }
  }
  
  return pages
})

// 方法
const loadFeedbacks = async () => {
  loading.value = true
  try {
    const response = await feedbackApi.getAllFeedbacks(currentPage.value, pageSize.value)
    feedbacks.value = response.data
    totalCount.value = response.total
  } catch (error) {
    console.error('加载反馈数据失败:', error)
    toast.error('加载反馈数据失败')
  } finally {
    loading.value = false
  }
}

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    loadFeedbacks()
  }
}

const resetFilters = () => {
  statusFilter.value = ''
  typeFilter.value = ''
  searchQuery.value = ''
  currentPage.value = 1
  loadFeedbacks()
}

const debounceSearch = (() => {
  let timeout: ReturnType<typeof setTimeout>
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      currentPage.value = 1
      loadFeedbacks()
    }, 500)
  }
})()

const viewFeedback = async (feedback: Feedback) => {
  try {
    const detail = await feedbackApi.getFeedbackDetail(feedback.id)
    selectedFeedback.value = detail
    showDetailModal.value = true
  } catch (error) {
    console.error('获取反馈详情失败:', error)
    toast.error('获取反馈详情失败')
  }
}

const handleFeedback = (feedback: Feedback | null) => {
  if (!feedback) return
  selectedFeedback.value = feedback
  handleForm.value = {
    status: feedback.status === 'pending' ? 'processing' : feedback.status as any,
    adminReply: feedback.adminReply || ''
  }
  showHandleModal.value = true
  showDetailModal.value = false
}

const submitHandle = async () => {
  if (!selectedFeedback.value) return
  
  saving.value = true
  try {
    await feedbackApi.updateFeedbackStatus(selectedFeedback.value.id, handleForm.value)
    toast.success('反馈处理成功')
    closeHandleModal()
    loadFeedbacks()
  } catch (error) {
    console.error('处理反馈失败:', error)
    toast.error('处理反馈失败')
  } finally {
    saving.value = false
  }
}

const closeDetailModal = () => {
  showDetailModal.value = false
  selectedFeedback.value = null
}

const closeHandleModal = () => {
  showHandleModal.value = false
  selectedFeedback.value = null
  handleForm.value = {
    status: 'processing',
    adminReply: ''
  }
}

// 题目详情相关方法
const showQuestionDetail = async (questionId: number, autoEdit: boolean = false) => {
  try {
    loadingQuestion.value = true
    questionNotFound.value = false
    editingQuestion.value = false
    showQuestionModal.value = true
    
    // 获取题目详情
    const questionData = await questionBanksApi.getQuestion(questionId)
    selectedQuestion.value = questionData
    
    // 获取题库信息
    if (questionData.questionBankId) {
      try {
        const questionBankData = await questionBanksApi.getQuestionBank(questionData.questionBankId)
        selectedQuestionBank.value = questionBankData
        console.log('获取到的题库数据:', questionBankData) // 调试日志
      } catch (bankError) {
        console.error('获取题库信息失败:', bankError)
        selectedQuestionBank.value = null
      }
    } else {
      selectedQuestionBank.value = null
    }
    
    console.log('获取到的题目数据:', questionData) // 调试日志
    console.log('题库ID:', questionData.questionBankId) // 调试日志
    if (autoEdit) editingQuestion.value = true
  } catch (error: any) {
    console.error('获取题目详情失败:', error)
    
    // 判断是否为题目不存在的错误
    if (error.response?.status === 404) {
      questionNotFound.value = true
      toast.error('题目不存在，可能已被删除')
    } else {
      toast.error('获取题目详情失败')
      showQuestionModal.value = false
    }
  } finally {
    loadingQuestion.value = false
  }
}

const closeQuestionModal = () => {
  showQuestionModal.value = false
  selectedQuestion.value = null
  selectedQuestionBank.value = null
  editingQuestion.value = false
  questionNotFound.value = false
}

// getQuestionTypeText 函数已从 @/utils/questionTypes 导入

// 难度显示
const getDifficultyText = (difficulty: number) => {
  const difficultyMap: Record<number, { text: string, class: string }> = {
    1: { text: '简单', class: 'success' },
    2: { text: '中等', class: 'warning' },
    3: { text: '困难', class: 'danger' }
  }
  return difficultyMap[difficulty] || { text: '未知', class: 'secondary' }
}

// 格式化答案显示
const formatAnswer = (answer: string, options?: Record<string, string>) => {
  if (!answer) return '无'
  
  // 如果有选项，尝试显示选项内容
  if (options) {
    const answerKeys = answer.split(',').map(key => key.trim())
    const answerTexts = answerKeys.map(key => {
      const optionText = options[key]
      return optionText ? `${key}. ${optionText}` : key
    })
    return answerTexts.join('; ')
  }
  
  return answer
}

// 编辑功能相关方法
const toggleEditMode = () => {
  editingQuestion.value = !editingQuestion.value
}

const saveQuestionEdit = async () => {
  if (!selectedQuestion.value) return

  const validationMessage = getChoiceAnswerValidationMessage(selectedQuestion.value)
  if (validationMessage) return void toast.error(validationMessage)
  
  try {
    loadingQuestion.value = true

    const { id, type, question, options, answer, explanation } = selectedQuestion.value

    // 调用更新题目API
    await questionBanksApi.updateQuestion(id, { type, question, options, answer, explanation })
    
    toast.success('题目更新成功')
    editingQuestion.value = false
  } catch (error) {
    console.error('更新题目失败:', error)
    toast.error('更新题目失败')
  } finally {
    loadingQuestion.value = false
  }
}

const cancelEdit = () => {
  editingQuestion.value = false
  // 重新获取题目数据以恢复原始状态
  if (selectedQuestion.value?.id) {
    showQuestionDetail(selectedQuestion.value.id)
  }
}

// 工具函数
const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN')
}

const getTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    question_error: '题目描述有误',
    answer_error: '答案有误',
    explanation_error: '解析有误',
    other: '其他问题'
  }
  return labels[type] || type
}

const getTypeBadgeClass = (type: string) => {
  const classes: Record<string, string> = {
    question_error: 'bg-warning',
    answer_error: 'bg-danger',
    explanation_error: 'bg-info',
    other: 'bg-secondary'
  }
  return classes[type] || 'bg-secondary'
}

const getStatusLabel = (status: string) => {
  const labels: Record<string, string> = {
    pending: '待处理',
    processing: '处理中',
    resolved: '已解决',
    rejected: '已拒绝'
  }
  return labels[status] || status
}

const getStatusBadgeClass = (status: string) => {
  const classes: Record<string, string> = {
    pending: 'bg-warning',
    processing: 'bg-primary',
    resolved: 'bg-success',
    rejected: 'bg-danger'
  }
  return classes[status] || 'bg-secondary'
}

// 生命周期
onMounted(() => {
  loadFeedbacks()
})
</script>

<style scoped>
.feedback-management {
  padding: 1.5rem;
  background-color: #F0F9FF;
  min-height: 100vh;
}

.page-header {
  background: white;
  color: #0C4A6E;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.1), 0 2px 4px -1px rgba(14, 165, 233, 0.06);
  border-left: 4px solid #0EA5E9;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
  color: #0C4A6E;
}

.page-description {
  font-size: 0.95rem;
  color: #475569;
  margin-bottom: 0;
}

.filters-section {
  background: white;
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(14, 165, 233, 0.05);
  border: 1px solid #E0F2FE;
  margin-bottom: 1.5rem;
}

.filters-section .form-control,
.filters-section .form-select {
  border-radius: 8px;
  border: 1px solid #BAE6FD;
  padding: 0.6rem 1rem;
  font-size: 0.95rem;
  transition: all 0.2s;
}

.filters-section .form-control:focus,
.filters-section .form-select:focus {
  border-color: #0EA5E9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.feedback-list {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.05);
  border: 1px solid #E0F2FE;
}

.table {
  margin-bottom: 0;
  font-size: 0.95rem;
}

.table th {
  border-top: none;
  border-bottom: 2px solid #E0F2FE;
  font-weight: 600;
  color: #475569;
  padding: 1rem 0.75rem;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.table td {
  padding: 1rem 0.75rem;
  vertical-align: middle;
  border-bottom: 1px solid #F8FAFC;
  color: #334155;
  white-space: nowrap;
}

.table-hover tbody tr {
  transition: background-color 0.15s ease;
}

.table-hover tbody tr:hover {
  background-color: #F0F9FF;
}

.table th.col-actions,
.table td.col-actions {
  position: sticky;
  right: 0;
  background-color: white;
  z-index: 1;
  box-shadow: -2px 0 5px rgba(14, 165, 233, 0.05);
  width: 140px;
  min-width: 140px;
  text-align: center;
  white-space: nowrap;
}

.table th.col-name,
.table td.col-name {
  min-width: 200px;
  white-space: normal; /* 允许题目内容换行 */
}

.table-hover tbody tr:hover td.col-actions {
  background-color: #F0F9FF;
}

.user-info strong {
  color: #0C4A6E;
}

.question-info {
  max-width: 300px;
}

.question-preview {
  font-size: 0.875rem;
  color: #475569;
  margin: 0.25rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.content-preview {
  max-width: 250px;
  font-size: 0.875rem;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-state {
  padding: 3rem 1rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-weight: 500;
  letter-spacing: 0.025em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.bg-success { background-color: #ECFDF5 !important; color: #059669 !important; }
.bg-danger { background-color: #FEF2F2 !important; color: #DC2626 !important; }
.bg-warning { background-color: #FFFBEB !important; color: #D97706 !important; }
.bg-primary { background-color: #F0F9FF !important; color: #0284C7 !important; }
.bg-info { background-color: #F0FDFA !important; color: #059669 !important; }
.bg-secondary { background-color: #F1F5F9 !important; color: #475569 !important; }

/* 文字按钮样式 */
.btn-group-text {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.btn-text {
  background: white;
  border: 1px solid #E2E8F0;
  padding: 0.35rem 0.6rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(14, 165, 233, 0.05);
}

.btn-text:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.08);
}

.btn-text:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #F8FAFC;
}

.btn-text.text-primary { 
  color: #0284C7; 
  border-color: #BAE6FD;
  background-color: #F0F9FF;
}
.btn-text.text-primary:hover:not(:disabled) { 
  background-color: #E0F2FE;
  border-color: #38BDF8;
}

.btn-text.text-success { 
  color: #059669; 
  border-color: #A7F3D0;
  background-color: #ECFDF5;
}
.btn-text.text-success:hover:not(:disabled) { 
  background-color: #D1FAE5;
  border-color: #10B981;
}

/* 分页样式优化 */
.pagination {
  gap: 0.25rem;
}

.pagination .page-link {
  color: #475569;
  border: none;
  background-color: #F8FAFC;
  margin: 0;
  border-radius: 4px;
  min-width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  transition: all 0.2s;
}

.pagination .page-item.active .page-link {
  background-color: #0EA5E9;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.2);
}

.pagination .page-link:hover:not(:disabled) {
  color: #0EA5E9;
  background-color: #E0F2FE;
}

.pagination .page-item.disabled .page-link {
  background-color: #F1F5F9;
  color: #CBD5E1;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .feedback-management {
    padding: 1rem;
  }
  
  .page-header {
    padding: 1.25rem;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .filters-section,
  .feedback-list {
    padding: 1.25rem;
  }
  
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .question-info,
  .content-preview {
    max-width: 150px;
  }
}
</style>
